import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";

const menuItems = [
  { label: "Home", id: "home" },
  { label: "Clients", id: "clients" },
  { label: "About", id: "about" },
  { label: "Products", id: "products" },
  { label: "Spares", id: "spares" },
  { label: "Capabilities", id: "capabilities" },
  { label: "Certification", id: "certification" },
  { label: "Approval", id: "approval" },
  { label: "Services", id: "services" },
  { label: "Careers", id: "career" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 150) setShowNavbar(true);
      else setShowNavbar(false);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toKebab = (s) =>
    s
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[_\s]+/g, "-")
      .toLowerCase();

  const findSectionElement = (id, label) => {
    if (!id && !label) return null;

    const tries = [];

    if (id) {
      const low = id.toLowerCase();
      tries.push(id, low, toKebab(low));
      if (low.endsWith("s")) tries.push(low.slice(0, -1));
      else tries.push(low + "s");
    }

    if (label) {
      const lowLabel = label.toLowerCase();
      tries.push(lowLabel, toKebab(lowLabel));
    }

    const uniq = [...new Set(tries.filter(Boolean))];

    for (const cand of uniq) {
      const el = document.getElementById(cand);
      if (el) return el;
    }

    for (const cand of uniq) {
      const el = document.querySelector(`[id*="${cand}"]`);
      if (el) return el;
    }

    if (label) {
      const text = label.trim().toLowerCase();
      const headings = Array.from(document.querySelectorAll("h1,h2,h3,h4"));
      for (const h of headings) {
        const txt = (h.textContent || "").trim().toLowerCase();
        if (txt && (txt === text || txt.includes(text) || text.includes(txt))) {
          return h.closest("section, div") || h;
        }
      }
    }

    return null;
  };

  const scrollToSection = (id) => {
    const menu = menuItems.find((m) => m.id === id);
    const label = menu ? menu.label : id;

    if (location.pathname !== "/") {
      sessionStorage.setItem("scrollTo", id || label || "");
      navigate("/", { state: { scrollTo: id || label || "" } });
      setOpen(false);
      return;
    }

    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setOpen(false);
      return;
    }

    const el = findSectionElement(id, label);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        background: "#fff",
        color: "#2d2075",
        borderBottom: "1px solid #e0e0e0",
        transition: "transform 0.35s ease",
        transform: showNavbar ? "translateY(0)" : "translateY(-100%)",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          width: "100%",
          maxWidth: "1600px",
          mx: "auto",
          px: { xs: 1.2, sm: 2, md: 4, lg: 6 },
          minHeight: 62,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* LOGO */}
        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", mr: 2 }}
          onClick={() => scrollToSection("home")}
        >
          <img src="/logo.png" alt="Multitex" style={{ height: 45 }} />
        </Box>

        {/* DESKTOP MENU */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1.5 }}>
          {menuItems.map((item) => (
            <Button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              sx={{
                fontSize: "0.8rem",
                color: "#2e247c",
                px: 1.4,
                py: 0.7,
                textTransform: "none",
                "&:hover": {
                  background: "#2e247c",
                  color: "#fff",
                },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* MOBILE MENU ICON */}
        <IconButton
          sx={{ display: { xs: "flex", md: "none" } }}
          onClick={() => setOpen(true)}
        >
          <MenuIcon sx={{ fontSize: 28, color: "#2e247c" }} />
        </IconButton>
      </Toolbar>

      {/* MOBILE DRAWER */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <List sx={{ width: 240 }}>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => scrollToSection(item.id)}
                sx={{
                  "&:hover": {
                    background: "#2e247c",
                    color: "#fff",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "1rem",
                    fontWeight: 400, 
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
}
